var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as PopperJS from '@popperjs/core';
import { usePopupStack } from './hooks';
import { useLocalRef } from '@workday/canvas-kit-react/common';
export var Popper = React.forwardRef(function (_a, ref) {
    var _b = _a.portal, portal = _b === void 0 ? true : _b, _c = _a.open, open = _c === void 0 ? true : _c, elemProps = __rest(_a, ["portal", "open"]);
    if (!open) {
        return null;
    }
    return React.createElement(OpenPopper, __assign({ ref: ref, portal: portal }, elemProps));
});
var getElementFromRefOrElement = function (input) {
    if (input === null) {
        return undefined;
    }
    else if ('current' in input) {
        return input.current || undefined;
    }
    else {
        return input;
    }
};
// prevent unnecessary renders if popperOptions are not passed
var defaultPopperOptions = {};
// Popper bails early if `open` is false and React hooks cannot be called conditionally,
// so we're breaking out the open version into another component.
var OpenPopper = React.forwardRef(function (_a, ref) {
    var anchorElement = _a.anchorElement, getAnchorClientRect = _a.getAnchorClientRect, _b = _a.popperOptions, popperOptions = _b === void 0 ? defaultPopperOptions : _b, _c = _a.placement, popperPlacement = _c === void 0 ? 'bottom' : _c, onPlacementChange = _a.onPlacementChange, children = _a.children, portal = _a.portal, popperInstanceRef = _a.popperInstanceRef;
    var firstRender = React.useRef(true);
    var _d = useLocalRef(popperInstanceRef), localRef = _d.localRef, elementRef = _d.elementRef;
    var _e = React.useState(popperPlacement), placement = _e[0], setPlacement = _e[1];
    var stackRef = usePopupStack(ref, anchorElement);
    var placementModifier = React.useMemo(function () {
        return {
            name: 'setPlacement',
            enabled: true,
            phase: 'main',
            fn: function (_a) {
                var state = _a.state;
                setPlacement(state.placement);
                onPlacementChange === null || onPlacementChange === void 0 ? void 0 : onPlacementChange(state.placement);
            },
        };
    }, [setPlacement, onPlacementChange]);
    // useLayoutEffect prevents flashing of the popup before position is determined
    React.useLayoutEffect(function () {
        var anchorEl = getAnchorClientRect
            ? { getBoundingClientRect: getAnchorClientRect }
            : getElementFromRefOrElement(anchorElement !== null && anchorElement !== void 0 ? anchorElement : null);
        if (!anchorEl) {
            console.warn("Popper: neither anchorElement or getAnchorClientRect was defined. A valid anchorElement or getAnchorClientRect callback must be provided to render a Popper");
            return undefined;
        }
        if (stackRef.current) {
            var instance_1 = PopperJS.createPopper(anchorEl, stackRef.current, __assign(__assign({ placement: popperPlacement }, popperOptions), { modifiers: __spreadArrays((popperOptions.modifiers || []), [placementModifier]) }));
            elementRef(instance_1); // update the ref with the instance
            return function () {
                instance_1 === null || instance_1 === void 0 ? void 0 : instance_1.destroy();
            };
        }
        return undefined;
        // We will maintain our own list of dependencies. We need to separate "create" and "update"
        // prop dependencies. We do _not_ want to destroy the Popper instance if options or placement
        // change, only if anchor or target refs change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorElement, getAnchorClientRect, stackRef]);
    React.useLayoutEffect(function () {
        var _a;
        // Only update options if this is _not_ the first render
        if (!firstRender.current) {
            (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.setOptions(__assign(__assign({ placement: popperPlacement }, popperOptions), { modifiers: __spreadArrays((popperOptions.modifiers || []), [placementModifier]) }));
        }
        firstRender.current = false;
    }, [popperOptions, popperPlacement, placementModifier, localRef]);
    var contents = React.createElement(React.Fragment, null, isRenderProp(children) ? children({ placement: placement }) : children);
    if (!portal) {
        return contents;
    }
    return ReactDOM.createPortal(contents, stackRef.current);
});
// Typescript threw an error about non-callable signatures. Using typeof as a 'function' returns
// a type of `Function` which isn't descriptive enough for Typescript. We don't do any detection
// against the _type_ of function that gets passed, but we'll assume it is a render prop for now...
function isRenderProp(children) {
    if (typeof children === 'function') {
        return true;
    }
    return false;
}
